import React, { useCallback, useEffect, useState } from "react";
import DesktopHeader from "../../components/DesktopHeader";
import { useAppDispatch, useAppSelector, useDebounce } from "../../app/hooks";
import { selectLabels } from "../../slices/language";
import DesktopBody from "../../components/DesktopBody";
import api from "../../api";
import {
  Button,
  CircularProgress,
  IconButton,
  Modal,
  Paper,
} from "@mui/material";
import { DesktopFiltersProps } from "../../components/DesktopFilters/DesktopFilters";
import {
  selectHasUserPropertySetting,
  selectUser,
  selectUserRole,
} from "../../slices/user";
import DesktopDropView from "../../components/DesktopDropView";
import NewAccessGroupForm from "../../components/NewAccessGroupForm";
import { AccessGroupsData } from "../../api/models/access-groups";
import AccessGroupComponent from "../../components/AccessGroupComponent";
import IconifyIcon from "../../components/IconifyIcon";
import BlockUserAccessGroup from "../../components/BlockUserAccessGroup";
import BlockedUsersAccessGroup from "../../components/BlockedUsersAccessGroups/BlockedUsersAccessGroups";
import { showError } from "../../utils/helpers";

const AccessGroups = () => {
  const labels = useAppSelector(selectLabels);
  const user = useAppSelector(selectUser);
  const dispatch = useAppDispatch();
  const userRole = useAppSelector(selectUserRole);
  const hasUserAccessGroupsPerms = useAppSelector(
    selectHasUserPropertySetting("user_access_groups"),
  );
  const hasUserAccessGroups = hasUserAccessGroupsPerms && userRole === "host";

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [accessGroups, setAccessGroups] = useState<AccessGroupsData[]>([]);
  const [completeAccessGroups, setCompleteAccessGroups] = useState<
    AccessGroupsData[]
  >([]);
  const [searchFilter, setSearchFilter] = useState<string>("");
  const [newAccessGroup, setNewAccessGroup] = useState<boolean>(false);
  const [blockUserModal, setBlockUserModal] = useState<boolean>(false);
  const [selectedAccessGroup, setSelectedAccessGroup] =
    useState<AccessGroupsData | null>(null);
  const [blockedUsersModal, setBlockedUsersModal] = useState<boolean>(false);
  const [deleteAccessGroupModal, setDeleteAccessGroupModal] =
    useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<"block" | "unblock">("block");

  const debouncedSearchText = useDebounce(searchFilter, 500);

  const accessPointsArray = user?.access_points
    ? user.access_points.map((item) => {
        return {
          label: item?.name,
          value: `${item.id}`,
        };
      })
    : [];

  accessPointsArray.unshift({
    label: labels.all_access_points,
    value: "0",
  });

  const filters: DesktopFiltersProps = {
    filterData: [
      {
        type: "search",
        value: searchFilter,
        setValue: setSearchFilter,
        disabled: isLoading,
        tooltip: "",
        function: () => {},
        array: [],
        size: "small",
        icon: "",
        label: "",
        multiDateValue: null,
        setMultiDateValue: () => {},
        filterWidth: 350,
      },
      // {
      //   type: "function",
      //   value: "",
      //   setValue: () => {},
      //   disabled: isLoading,
      //   tooltip: labels.download_content,
      //   function: () => {},
      //   array: [],
      //   size: "small",
      //   icon: "fa-solid:file-download",
      //   label: "",
      //   multiDateValue: null,
      //   setMultiDateValue: () => {},
      // },
      {
        type: "function",
        value: "",
        setValue: () => {},
        disabled: isLoading,
        tooltip: "",
        function: () => setNewAccessGroup(true),
        array: [],
        size: "small",
        icon: "fa6-solid:circle-plus",
        label: "",
        multiDateValue: null,
        setMultiDateValue: () => {},
      },
    ],
  };

  const getAccessGroups = useCallback(
    async (debouncedSearchText: string) => {
      if (user?.property?.id) {
        try {
          setIsLoading(true);
          if (hasUserAccessGroups) {
            const response = await api.accessGroupsRequest(
              Number(user?.property?.id),
              1,
              100,
              true,
              debouncedSearchText,
            );
            if (response) {
              setAccessGroups(response?.data);
            }
            const responseComplete = await api.accessGroupsRequest(
              Number(user?.property?.id),
              1,
              100,
              false,
              debouncedSearchText,
            );
            if (responseComplete) {
              setCompleteAccessGroups(responseComplete?.data);
            }
          } else {
            const response = await api.accessGroupsRequest(
              Number(user?.property?.id),
              1,
              100,
              false,
              debouncedSearchText,
            );
            if (response) {
              setAccessGroups(response?.data);
            }
          }
        } catch (error: any) {
          console.error(error);
          const errorMessage = error?.response?.data?.error?.message;
          showError(
            dispatch,
            errorMessage ? errorMessage : labels.generic_error,
          );
        } finally {
          setIsLoading(false);
        }
      }
    },
    [dispatch, hasUserAccessGroups, labels.generic_error, user?.property?.id],
  );

  useEffect(() => {
    getAccessGroups(debouncedSearchText);
  }, [debouncedSearchText, getAccessGroups]);

  const onDeleteAccessGroup = async () => {
    if (selectedAccessGroup) {
      try {
        setIsLoading(true);
        await api.deleteAccessGroupRequest(selectedAccessGroup?.documentId);
        setDeleteAccessGroupModal(false);
        setSelectedAccessGroup(null);
        getAccessGroups(debouncedSearchText);
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="w-100">
      <Modal
        open={deleteAccessGroupModal}
        onClose={() => setDeleteAccessGroupModal(false)}
      >
        <div className="w-100 h-100 flex align-items-center justify-content-center">
          <Paper className="border-box flex flex-dir-col gap-6 p-8">
            <div className="w-100 flex align-items-center justify-content-between mb-2">
              <p className="t-nowrap body-text-lg font-w-500 title-color">
                {labels.delete_access_group}
              </p>
              <IconButton
                onClick={() => {
                  setDeleteAccessGroupModal(false);
                  setSelectedAccessGroup(null);
                }}
              >
                <IconifyIcon icon={"mingcute:close-fill"} />
              </IconButton>
            </div>
            <div
              className="w-100 h-100"
              style={{
                borderRadius: 4,
                overflow: "hidden",
                boxSizing: "border-box",
                maxWidth: "100%",
              }}
            >
              <p>
                {labels.del_acc_grp_text}{" "}
                <span className="primary-color font-w-700">
                  {selectedAccessGroup?.name}
                </span>
                ?
              </p>
              <p
                className="mt-5"
                style={{ color: "#bd190d", textAlign: "center" }}
              >
                {labels.not_reversible_action}
              </p>
              <div className="w-100 mt-10">
                <Button
                  fullWidth
                  variant="contained"
                  onClick={onDeleteAccessGroup}
                >
                  {labels.delete}
                </Button>
              </div>
            </div>
          </Paper>
        </div>
      </Modal>
      <Modal
        open={blockedUsersModal}
        onClose={() => setBlockedUsersModal(false)}
      >
        <div className="w-100 h-100 flex align-items-center justify-content-center">
          <Paper
            className="border-box flex flex-dir-col gap-6 p-8"
            // style={{ height: "90vh" }}
          >
            <div className="w-100 flex align-items-center justify-content-between mb-2">
              <p className="t-nowrap body-text-lg font-w-500 title-color">
                {labels.blocked_users}
              </p>
              <IconButton
                onClick={() => {
                  setBlockedUsersModal(false);
                  setSelectedAccessGroup(null);
                  setSelectedTab("block");
                }}
              >
                <IconifyIcon icon={"mingcute:close-fill"} />
              </IconButton>
            </div>
            <div className="w-100 border-box flex align-items-center justify-content-evenly">
              <p
                className="body-text-sm uppercase py-2 px-6 font-w-500"
                style={{
                  borderRadius: 4,
                  color: selectedTab === "block" ? "#10418a" : "",
                  cursor: selectedTab === "block" ? "default" : "pointer",
                }}
                onClick={() => setSelectedTab("block")}
              >
                {labels.block}
              </p>
              <p
                className="body-text-sm uppercase py-2 px-6 font-w-500"
                style={{
                  borderRadius: 4,
                  color: selectedTab === "unblock" ? "#10418a" : "",
                  cursor: selectedTab === "unblock" ? "default" : "pointer",
                }}
                onClick={() => setSelectedTab("unblock")}
              >
                {labels.unblock}
              </p>
            </div>
            {selectedTab === "block" && (
              <div
                className="w-100 h-100"
                style={{
                  borderRadius: 4,
                  overflow: "hidden",
                  boxSizing: "border-box",
                }}
              >
                <BlockUserAccessGroup
                  selectedAccessGroup={selectedAccessGroup}
                  extraFunc={() => {
                    setBlockedUsersModal(false);
                    setSelectedAccessGroup(null);
                    getAccessGroups(debouncedSearchText);
                    setSelectedTab("block");
                  }}
                />
              </div>
            )}
            {selectedTab === "unblock" && (
              <div
                className="w-100 h-100"
                style={{
                  borderRadius: 4,
                  overflow: "hidden",
                  boxSizing: "border-box",
                }}
              >
                <BlockedUsersAccessGroup
                  selectedAccessGroup={selectedAccessGroup}
                  extraFunc={() => {
                    setBlockedUsersModal(false);
                    setSelectedAccessGroup(null);
                    getAccessGroups(debouncedSearchText);
                    setSelectedTab("block");
                  }}
                />
              </div>
            )}
          </Paper>
        </div>
      </Modal>
      <Modal open={blockUserModal} onClose={() => setBlockUserModal(false)}>
        <div className="w-100 h-100 flex align-items-center justify-content-center">
          <Paper className="border-box flex flex-dir-col gap-6 p-8">
            <div className="w-100 flex align-items-center justify-content-between mb-2">
              <p className="t-nowrap body-text-lg font-w-500 title-color">
                {labels.block_user}
              </p>
              <IconButton
                onClick={() => {
                  setBlockUserModal(false);
                  setSelectedAccessGroup(null);
                }}
              >
                <IconifyIcon icon={"mingcute:close-fill"} />
              </IconButton>
            </div>
            <div
              className="w-100 h-100"
              style={{
                borderRadius: 4,
                overflow: "hidden",
                boxSizing: "border-box",
              }}
            >
              <BlockUserAccessGroup
                selectedAccessGroup={selectedAccessGroup}
                extraFunc={() => {
                  setBlockUserModal(false);
                  setSelectedAccessGroup(null);
                  getAccessGroups(debouncedSearchText);
                }}
              />
            </div>
          </Paper>
        </div>
      </Modal>
      <DesktopHeader title={labels.access_groups} />
      <DesktopBody hasFilters={true} filterData={filters.filterData}>
        <div className="w-100 h-100 px-8 pb-8 border-box">
          {isLoading ? (
            <div className="w-100 h-100 border-box flex align-items-center justify-content-center">
              <CircularProgress />
            </div>
          ) : (
            <div className="w-100 flex flex-dir-col gap-8 pb-8">
              {accessGroups && accessGroups.length > 0 ? (
                accessGroups.map((item, index) => (
                  <div key={`${item?.id}-${index}`} className="w-100">
                    <AccessGroupComponent
                      hasUserAccessGroups={hasUserAccessGroups}
                      hasExitRestriction={item?.configuration?.some(
                        (config) =>
                          config?.exit_restriction_from ||
                          config?.exit_restriction_to ||
                          config?.exit_restriction,
                      )}
                      accessGroup={item}
                      blockFunction={() => {
                        setSelectedAccessGroup(item);
                        setBlockUserModal(true);
                      }}
                      seeBlockedUsersFunc={() => {
                        setSelectedAccessGroup(item);
                        setBlockedUsersModal(true);
                      }}
                      deleteFunction={() => {
                        setSelectedAccessGroup(item);
                        setDeleteAccessGroupModal(true);
                      }}
                      editFunction={() => {
                        setSelectedAccessGroup(item);
                        setNewAccessGroup(true);
                      }}
                    />
                  </div>
                ))
              ) : (
                <div className="w-100">
                  <p
                    className="body-text-lg font-w-500 title-color text-center"
                    style={{ marginTop: 40 }}
                  >
                    {labels.no_access_groups}
                  </p>
                  <p
                    className="body-text-sm font-w-500 title-color text-center"
                    style={{ marginTop: 10 }}
                  >
                    {labels.create_access_group_to_start}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </DesktopBody>
      <DesktopDropView
        showView={newAccessGroup}
        setShowView={setNewAccessGroup}
        content={
          <div className="flex h-100 border-box">
            <div className="h-100 border-box p-8 scroll">
              {/* <RefactorAccessGroupForm
                currentAccessGroups={completeAccessGroups}
                selectedAccessGroup={selectedAccessGroup}
                isHostAccessGroup={hasUserAccessGroups}
              /> */}
              <NewAccessGroupForm
                currentAccessGroups={completeAccessGroups}
                hasUserAccessGroups={hasUserAccessGroups}
                showForm={newAccessGroup}
                extraFunc={() => {
                  setNewAccessGroup(false);
                  setSelectedAccessGroup(null);
                  setTimeout(() => {
                    getAccessGroups(debouncedSearchText);
                  }, 300);
                }}
                selectedAccessGroup={selectedAccessGroup}
              />
            </div>
          </div>
        }
        extraFunc={false}
        title={
          selectedAccessGroup
            ? labels.edit_access_group
            : labels.new_access_group
        }
        onCLoseFunc={() => {
          setSelectedAccessGroup(null);
        }}
      />
    </div>
  );
};

export default AccessGroups;
